import React, { useState } from "react";
import { Link } from "wouter";
import {
  Calendar,
  Users,
  MessageSquare,
  Clock,
  Star,
  CheckCircle,
  XCircle,
  ArrowRight,
  Filter,
  RefreshCw,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import {
  useCounselorStats,
  usePendingRequests,
} from "@/hooks/useCounselorData";
import {
  useApproveSessionRequest,
  useRejectSessionRequest,
} from "@/hooks/useSessions";

interface StatCard {
  title: string;
  value: string | number;
  // change: string;
  // trend: "up" | "down" | "neutral";
  icon: React.ElementType;
  color: string;
}

export const Dashboard: React.FC = () => {
  const { user, counselorProfile } = useAuth();
  const [selectedTimeframe, setSelectedTimeframe] = useState<
    "today" | "week" | "month" | "all"
  >("today");
  const [isRefreshing, setIsRefreshing] = useState(false);

  // API hooks
  const {
    data: counselorStats,
    isLoading: statsLoading,
    refetch: refetchStats,
  } = useCounselorStats(user?.id, selectedTimeframe);
  const {
    data: pendingRequests = [],
    isLoading: requestsLoading,
    refetch: refetchRequests,
  } = usePendingRequests(user?.id);

  // Mutations
  const approveRequest = useApproveSessionRequest();
  const rejectRequest = useRejectSessionRequest();

  // Use real data from API or fallback to default values
  const stats: StatCard[] = [
    {
      title: "Connected Students",
      value: counselorStats?.connectedStudents || 0,
      icon: Users,
      color: "blue",
    },
    {
      title: "Total Sessions",
      value: counselorStats?.totalSessions || 0,
      icon: Calendar,
      color: "green",
    },
    {
      title: "Avg. Rating",
      value: counselorStats?.averageRating || 0,
      icon: Star,
      color: "yellow",
    },
    {
      title: "Total Call Duration",
      value: `${Math.round(counselorStats?.audioAndVideoCallDuration || 0)}m`,
      icon: Clock,
      color: "purple",
    },
  ];

  const handleActionApprove = async (actionId: string, action: any) => {
    try {
      if (action.type === "session_request") {
        await approveRequest.mutateAsync({
          requestId: actionId,
          data: {
            scheduledAt: action.dueDate,
            notes: "Approved from dashboard",
          },
        });
        // Invalidate pending requests to refresh the list
        await refetchRequests();
      }
      // Handle other action types as needed
    } catch (error) {
      console.error("Error approving action:", error);
    }
  };

  const handleActionReject = async (actionId: string, action: any) => {
    try {
      if (action.type === "session_request") {
        await rejectRequest.mutateAsync({
          requestId: actionId,
          reason: "Rejected from dashboard",
        });
        // Invalidate pending requests to refresh the list
        await refetchRequests();
      }
      // Handle other action types as needed
    } catch (error) {
      console.error("Error rejecting action:", error);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([refetchStats(), refetchRequests()]);
    } catch (error) {
      console.error("Error refreshing data:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Show loading state
  if (statsLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Welcome back, {counselorProfile?.firstName || "Counselor"}!
          </h1>
          <p className="text-gray-600 mt-1">
            Here's what's happening with your students today
          </p>
        </div>
        <div className="flex items-center space-x-3 mt-4 sm:mt-0">
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className={cn(
              "flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors",
              isRefreshing && "opacity-50 cursor-not-allowed"
            )}
          >
            <RefreshCw
              className={cn("h-4 w-4", isRefreshing && "animate-spin")}
            />
            <span>Refresh</span>
          </button>
          <div className="flex bg-white border border-gray-300 rounded-lg p-1">
            {(["today", "week", "month", "all"] as const).map((timeframe) => (
              <button
                key={timeframe}
                onClick={() => setSelectedTimeframe(timeframe)}
                className={cn(
                  "px-3 py-1 rounded-md text-sm font-medium transition-colors",
                  selectedTimeframe === timeframe
                    ? "bg-primary text-white"
                    : "text-gray-600 hover:text-gray-900"
                )}
              >
                {timeframe.charAt(0).toUpperCase() + timeframe.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <div
            key={index}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  {stat.title}
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-2">
                  {stat.value}
                </p>
                {/* <div className="flex items-center mt-2">
                  <TrendingUp
                    className={cn(
                      "h-4 w-4 mr-1",
                      stat.trend === "up" ? "text-green-500" : "text-red-500"
                    )}
                  />
                  <span
                    className={cn(
                      "text-sm font-medium",
                      stat.trend === "up" ? "text-green-600" : "text-red-600"
                    )}
                  >
                    {stat.change}
                  </span>
                  <span className="text-sm text-gray-500 ml-1">
                    from last {selectedTimeframe}
                  </span>
                </div> */}
              </div>
              <div
                className={cn(
                  "p-3 rounded-lg",
                  stat.color === "blue" && "bg-blue-100",
                  stat.color === "green" && "bg-green-100",
                  stat.color === "yellow" && "bg-yellow-100",
                  stat.color === "purple" && "bg-purple-100"
                )}
              >
                <stat.icon
                  className={cn(
                    "h-6 w-6",
                    stat.color === "blue" && "text-blue-600",
                    stat.color === "green" && "text-green-600",
                    stat.color === "yellow" && "text-yellow-600",
                    stat.color === "purple" && "text-purple-600"
                  )}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Pending Actions */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">
                  Pending Actions
                </h2>
                <div className="flex items-center space-x-2">
                  <Filter className="h-4 w-4 text-gray-500" />
                  <span className="text-sm text-gray-500">
                    {pendingRequests.length} items
                  </span>
                  {requestsLoading && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                  )}
                </div>
              </div>
            </div>
            <div className="divide-y divide-gray-200">
              {pendingRequests.map((request) => {
                const ActionIcon = Calendar; // All pending requests are session requests
                return (
                  <div
                    key={request.id}
                    className="p-6 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-4 flex-1">
                        <div className="p-2 bg-gray-100 rounded-lg">
                          <ActionIcon className="h-5 w-5 text-gray-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-1">
                            <h3 className="text-sm font-medium text-gray-900">
                              Session Request
                            </h3>
                            <span
                              className={cn(
                                "px-2 py-1 text-xs font-medium rounded-full border",
                                "bg-orange-100 text-orange-800 border-orange-200"
                              )}
                            >
                              Pending
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mb-2">
                            {request.description}
                          </p>
                          <div className="flex items-center space-x-4 text-xs text-gray-500">
                            <span>
                              {new Date(request.createdAt).toLocaleString()}
                            </span>
                            <span>Student: {request.studentName}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        <button
                          onClick={() =>
                            handleActionApprove(request.id, {
                              type: "session_request",
                              dueDate: new Date().toISOString(),
                            })
                          }
                          disabled={approveRequest.isPending}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50"
                          title="Approve"
                        >
                          {approveRequest.isPending ? (
                            <div className="animate-spin rounded-full size-5 border-b-2 border-green-600"></div>
                          ) : (
                            <CheckCircle className="size-5" />
                          )}
                        </button>
                        <button
                          onClick={() =>
                            handleActionReject(request.id, {
                              type: "session_request",
                            })
                          }
                          disabled={rejectRequest.isPending}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                          title="Reject"
                        >
                          {rejectRequest.isPending ? (
                            <div className="animate-spin rounded-full size-5 border-b-2 border-red-600"></div>
                          ) : (
                            <XCircle className="size-5" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {pendingRequests.length === 0 && !requestsLoading && (
              <div className="p-12 text-center">
                <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-3" />
                <h3 className="text-lg font-medium text-gray-900 mb-1">
                  All caught up!
                </h3>
                <p className="text-gray-600">
                  No pending actions at the moment.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions & Recent Activity */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Quick Actions
            </h3>
            <div className="space-y-3">
              <Link href="/students">
                <button className="w-full flex items-center justify-between p-3 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors text-left">
                  <div className="flex items-center space-x-3">
                    <Users className="h-5 w-5 text-blue-600" />
                    <span className="font-medium text-blue-900">
                      View All Students
                    </span>
                  </div>
                  <ArrowRight className="h-4 w-4 text-blue-600" />
                </button>
              </Link>
              {/* <Link href="/sessions">
                <button className="w-full flex items-center justify-between p-3 bg-green-50 hover:bg-green-100 rounded-lg transition-colors text-left">
                  <div className="flex items-center space-x-3">
                    <Calendar className="h-5 w-5 text-green-600" />
                    <span className="font-medium text-green-900">Schedule Session</span>
                  </div>
                  <ArrowRight className="h-4 w-4 text-green-600" />
                </button>
              </Link> */}
              <Link href="/messages">
                <button className="w-full flex items-center justify-between p-3 bg-yellow-50 hover:bg-yellow-100 rounded-lg transition-colors text-left">
                  <div className="flex items-center space-x-3">
                    <MessageSquare className="h-5 w-5 text-yellow-600" />
                    <span className="font-medium text-yellow-900">
                      Check Messages
                    </span>
                  </div>
                  <ArrowRight className="h-4 w-4 text-yellow-600" />
                </button>
              </Link>
            </div>
          </div>

          {/* Recent Activity */}
          {/* <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Recent Activity
            </h3>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                <div className="flex-1">
                  <p className="text-sm text-gray-900">
                    Session completed with John Doe
                  </p>
                  <p className="text-xs text-gray-500">2 hours ago</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                <div className="flex-1">
                  <p className="text-sm text-gray-900">
                    New student added: Emma Wilson
                  </p>
                  <p className="text-xs text-gray-500">4 hours ago</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-purple-500 rounded-full mt-2"></div>
                <div className="flex-1">
                  <p className="text-sm text-gray-900">
                    Profile updated successfully
                  </p>
                  <p className="text-xs text-gray-500">1 day ago</p>
                </div>
              </div>
            </div>
          </div> */}
        </div>
      </div>
    </div>
  );
};
